import { useMemo, useState } from "react";
import { usePlatformStore } from "@platform/store";
import {
  useFetchPendingInvitations,
  useClaimPendingInvitation,
  INVITATION_TYPE,
} from "@core/features/invitation";
import { useFetchUserPrograms, PROGRAM_ROLE_SLUG } from "@core/features/program";
import { PendingInvitationsList } from "./PendingInvitationsList";

export function PendingInvitationsListContainer() {
  const user = usePlatformStore((s) => s.user);
  const [claimingShortcode, setClaimingShortcode] = useState<string | null>(
    null,
  );

  const { data: allInvitations = [] } = useFetchPendingInvitations({ user });
  const { data: programsData } = useFetchUserPrograms({ user });
  const { mutateAsync: claimInvitation } = useClaimPendingInvitation();

  // Build set of program IDs where the user is already a participant
  const participantProgramIds = useMemo(() => {
    const programs = programsData?.programs ?? [];
    return new Set(
      programs
        .filter((p) => p.user_roles?.includes(PROGRAM_ROLE_SLUG.ENROLLED_PARTICIPANT))
        .map((p) => p.id),
    );
  }, [programsData?.programs]);

  // Filter out participant invitations for programs where user is already a participant
  const invitations = useMemo(() => {
    return allInvitations.filter(
      (inv) =>
        !(
          participantProgramIds.has(inv.program_id) &&
          (inv.invitation_type === INVITATION_TYPE.PARTICIPANT ||
            inv.invitation_type === INVITATION_TYPE.PARTICIPANT_FRIEND)
        ),
    );
  }, [allInvitations, participantProgramIds]);

  const handleClaim = async (shortcode: string) => {
    setClaimingShortcode(shortcode);
    try {
      await claimInvitation({ shortcode });
    } finally {
      setClaimingShortcode(null);
    }
  };

  return (
    <PendingInvitationsList
      invitations={invitations}
      onClaim={handleClaim}
      claimingShortcode={claimingShortcode}
    />
  );
}

export default PendingInvitationsListContainer;
